import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  BaseLayout,
  SkeletonLoader,
  Alert,
  Badge,
  Authorizor,
  Spinner,
} from '@/components';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
} from '@/components/ui';
import {
  BADGE_TYPES,
  ALERT_TYPES,
  TOAST_TYPES,
} from '@/constants/common.constants';
import {
  SURVEY_ALERT_TYPE_LABELS,
  SURVEY_POSITIVE_SCORE_THRESHOLD,
  ENPS_QUESTION,
} from '@/constants/survey.constants';
import { isEnpsDetractor } from '@/utils/route-survey';
import { useToast } from '@/contexts/toast-context';
import { useAuth } from '@/hooks/use-auth';
import {
  getRouteSurveyByUuid,
  markRouteSurveyAsViewed,
  markRouteSurveyAsUnviewed,
  downloadRouteSurveyPdf,
} from '@/api/route-survey.api';
import type { RouteSurveyResponse } from '@/types/route-survey.types';
import { SurveyType } from '@/types/surveys.types';
import ROLES from '@/constants/roles.constants';
import { formatSurveyDate, formatSurveyTime } from '@/lib/utils';

import './route-surveys-view-style.scss';

export const Route = createFileRoute('/route-surveys/$uuid/')({
  component: RouteSurveyView,
});

function RouteSurveyView() {
  const { uuid } = Route.useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const [survey, setSurvey] = useState<RouteSurveyResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Fetch survey data on component mount
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        setIsLoading(true);
        const data = await getRouteSurveyByUuid(uuid);
        setSurvey(data);
      } catch (err) {
        addToast(err instanceof Error ? err.message : 'Failed to load survey', {
          type: TOAST_TYPES.ERROR,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSurvey();
  }, [uuid]);

  // Badge style based on alert type
  const getAlertTypeBadgeType = (alertType: string) => {
    const normalized = alertType.toLowerCase();
    if (normalized.includes('comment')) return BADGE_TYPES.WARNING;
    if (normalized.includes('score')) return BADGE_TYPES.DANGER;
    return BADGE_TYPES.NEUTRAL;
  };

  //Convert alert type to readable format
  const formatAlertType = (alertType: string) => {
    return SURVEY_ALERT_TYPE_LABELS[alertType] || alertType;
  };

  const navigateToSurvey = (s: { uuid: string; type: SurveyType }) => {
    if (s.type === SurveyType.ROUTE_SURVEY) {
      navigate({ to: `/route-surveys/${s.uuid}` });
    } else if (s.type === SurveyType.INCIDENT_REPORT) {
      navigate({ to: `/incident-reports/${s.uuid}` });
    } else if (s.type === SurveyType.CUSTOMER_FEEDBACK) {
      navigate({ to: `/customer-feedbacks/${s.uuid}` });
    }
  };

  const handlePreviousSurvey = () => {
    if (!survey?.previousSurvey) return;
    navigateToSurvey(survey.previousSurvey);
  };

  const handleNextSurvey = () => {
    if (!survey?.nextSurvey) return;
    navigateToSurvey(survey.nextSurvey);
  };

  const handleMarkAsViewed = async () => {
    if (!survey || !user) return;

    try {
      await markRouteSurveyAsViewed(uuid);
      addToast('Survey marked as reviewed successfully', {
        type: TOAST_TYPES.SUCCESS,
      });
      // Update the local state to reflect the change
      setSurvey({
        ...survey,
        viewed: true,
        viewedBy: {
          uuid: user.uuid,
          name: user.name,
        },
      });
    } catch (err) {
      addToast(
        err instanceof Error
          ? err.message
          : 'Failed to mark survey as reviewed',
        {
          type: TOAST_TYPES.ERROR,
        },
      );
    }
  };

  const handleMarkAsUnviewed = async () => {
    if (!survey || !user) return;

    // Only allow unmarking if current user is the one who marked it as viewed
    if (survey.viewedBy && survey.viewedBy.uuid !== user.uuid) {
      addToast(
        'Only the person who marked this as reviewed can mark it as not reviewed',
        {
          type: TOAST_TYPES.ERROR,
        },
      );
      return;
    }

    try {
      await markRouteSurveyAsUnviewed(uuid);
      addToast('Survey marked as not reviewed successfully', {
        type: TOAST_TYPES.SUCCESS,
      });
      // Update the local state to reflect the change
      setSurvey({ ...survey, viewed: false, viewedBy: null });
    } catch (err) {
      addToast(
        err instanceof Error
          ? err.message
          : 'Failed to mark survey as not reviewed',
        {
          type: TOAST_TYPES.ERROR,
        },
      );
    }
  };

  const handleDownloadPdf = async () => {
    setIsDownloadingPdf(true);
    try {
      await downloadRouteSurveyPdf(uuid);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to download PDF', {
        type: TOAST_TYPES.ERROR,
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <BaseLayout
      header={{
        headerText: 'Route Survey',
        headerChildren:
          survey && (survey.previousSurvey || survey.nextSurvey) ? (
            <div className="button-container-right route-survey-nav-buttons">
              {/* Navigation Buttons */}
              <Button
                variant="outline-secondary"
                size="small"
                onClick={handlePreviousSurvey}
                disabled={!survey.previousSurvey}
              >
                <span>←</span>
                <span className="route-survey-nav-text">Previous</span>
              </Button>
              <Button
                variant="outline-secondary"
                size="small"
                onClick={handleNextSurvey}
                disabled={!survey.nextSurvey}
              >
                <span className="route-survey-nav-text">Next</span>
                <span>→</span>
              </Button>
            </div>
          ) : undefined,
      }}
      breadcrumbs={{
        showBreadcrumbs: true,
        breadcrumbText: ['Route Survey'],
      }}
    >
      {isLoading ? (
        <SkeletonLoader count={10} height={30} />
      ) : !survey ? null : (
        <div className="route-survey-view">
          {/* Survey Overview Card */}
          <Card className="route-survey-view-overview-card">
            <CardContent>
              <div className="route-survey-view-overview">
                <Authorizor allowedRoles={[ROLES.ADMINISTRATOR]}>
                  <div className="route-survey-view-overview-item">
                    <div className="route-survey-view-overview-label">
                      Account
                    </div>
                    <div>{survey.accountName}</div>
                  </div>
                </Authorizor>
                <div className="route-survey-view-overview-item">
                  <span className="route-survey-view-overview-label">
                    Alert Type
                  </span>
                  <Badge
                    text={formatAlertType(survey.alertType)}
                    type={getAlertTypeBadgeType(survey.alertType)}
                  />
                </div>
                <div className="route-survey-view-overview-item">
                  <span className="route-survey-view-overview-label">
                    Survey Date
                  </span>
                  <span>{formatSurveyDate(survey.surveyDate)}</span>
                </div>
                <div className="route-survey-view-overview-item">
                  <span className="route-survey-view-overview-label">
                    Survey Time
                  </span>
                  <span>{formatSurveyTime(survey.surveyDate)}</span>
                </div>
                <Authorizor
                  allowedRoles={[
                    ROLES.ADMINISTRATOR,
                    ROLES.EXECUTIVE,
                    ROLES.MANAGER,
                    ROLES.STAFF,
                  ]}
                >
                  <div className="route-survey-view-overview-item">
                    <span className="route-survey-view-overview-label">
                      Driver
                    </span>
                    <span>
                      <a
                        className="link"
                        href={`/organizations/${survey.driver.uuid}`}
                        onClick={(e) => {
                          if (e.ctrlKey || e.metaKey) {
                            e.stopPropagation();
                          } else {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate({
                              to: `/organizations/${survey.driver.uuid}`,
                            });
                          }
                        }}
                      >
                        {survey.driver.name}
                      </a>
                      {survey.driver.status === 'deactivated' && (
                        <>
                          {' '}
                          <Badge text="Deactivated" type={BADGE_TYPES.DANGER} />
                        </>
                      )}
                    </span>
                  </div>
                  <div className="route-survey-view-overview-item">
                    <span className="route-survey-view-overview-label">
                      Manager
                    </span>
                    <span>{survey.manager}</span>
                  </div>
                </Authorizor>

                <div className="route-survey-view-overview-item">
                  <span className="route-survey-view-overview-label">
                    Overall Score
                  </span>
                  <span
                    className={`route-survey-view-overview-score ${
                      survey.overallScore < SURVEY_POSITIVE_SCORE_THRESHOLD
                        ? 'route-survey-view-overview-score-low'
                        : ''
                    }`}
                  >
                    {Math.round(survey.overallScore)}
                  </span>
                </div>
                <div className="route-survey-view-overview-item">
                  <span className="route-survey-view-overview-label">
                    Reviewed
                  </span>
                  <span
                    className={`${
                      survey.viewed
                        ? 'route-survey-view-overview-viewed'
                        : 'route-survey-view-overview-not-viewed'
                    }`}
                  >
                    {survey.viewed && survey.viewedBy
                      ? `Yes [${survey.viewedBy.name}]`
                      : 'No'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* eNPS Card */}
          <Card className="route-survey-view-category-card">
            <CardHeader>
              <CardTitle className="route-survey-view-category-header">
                eNPS:{' '}
                <span
                  className={
                    isEnpsDetractor(survey.enpsScore)
                      ? 'route-survey-view-category-score-low'
                      : 'route-survey-view-category-score'
                  }
                >
                  {survey.enpsScore}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="route-survey-view-questions">
                <div className="route-survey-view-question">
                  <div
                    className={
                      isEnpsDetractor(survey.enpsScore)
                        ? 'route-survey-view-question-negative'
                        : ''
                    }
                  >
                    {ENPS_QUESTION.text}
                  </div>

                  <div
                    className={
                      isEnpsDetractor(survey.enpsScore)
                        ? 'route-survey-view-answer-negative'
                        : 'route-survey-view-answer'
                    }
                  >
                    {survey.enpsScore}
                  </div>

                  {survey.enpsComments && (
                    <div>
                      <Alert
                        type={ALERT_TYPES.WARNING}
                        text={<>Comment: {survey.enpsComments}</>}
                        className="text-neutral-dark-1"
                      />
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Category Cards */}
          {survey.categories.map((category) => (
            <Card key={category.id} className="route-survey-view-category-card">
              <CardHeader>
                <CardTitle className="route-survey-view-category-header">
                  {category.name}:{' '}
                  <span
                    className={
                      category.score < SURVEY_POSITIVE_SCORE_THRESHOLD
                        ? 'route-survey-view-category-score-low'
                        : 'route-survey-view-category-score'
                    }
                  >
                    <span>{category.score}</span>
                    <span className="route-survey-view-category-score-total">
                      /100
                    </span>
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="route-survey-view-questions">
                  {category.questions.map((question) => {
                    const answer = question.userAnswer;
                    if (!answer) return null;

                    const isNegativeScore =
                      answer.score <= SURVEY_POSITIVE_SCORE_THRESHOLD;

                    return (
                      <div
                        key={question.id}
                        className="route-survey-view-question"
                      >
                        <div
                          className={`${
                            isNegativeScore
                              ? 'route-survey-view-question-negative'
                              : ''
                          }`}
                        >
                          {question.text}
                        </div>

                        <div
                          className={
                            isNegativeScore
                              ? 'route-survey-view-answer-negative'
                              : 'route-survey-view-answer'
                          }
                        >
                          {answer.optionLabel} ({answer.score})
                        </div>

                        {answer.comment && (
                          <div>
                            <Alert
                              type={ALERT_TYPES.WARNING}
                              text={<>Comment: {answer.comment}</>}
                              className="text-neutral-dark-1"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
          <div className="button-container-right">
            {/* Download PDF and Mark as reviewed/ not reviewed */}
            <Authorizor
              allowedRoles={[
                ROLES.ADMINISTRATOR,
                ROLES.EXECUTIVE,
                ROLES.MANAGER,
                ROLES.STAFF,
              ]}
            >
              <Button
                variant="outline-secondary"
                size="medium"
                onClick={handleDownloadPdf}
                disabled={isDownloadingPdf}
              >
                {isDownloadingPdf && <Spinner size="small" />}
                Download PDF
              </Button>
            </Authorizor>
            {/* Mark as reviewed/ not reviewed */}
            <Authorizor
              allowedRoles={[
                ROLES.ADMINISTRATOR,
                ROLES.EXECUTIVE,
                ROLES.MANAGER,
                ROLES.STAFF,
              ]}
            >
              {survey.canMarkAsViewed && (
                <>
                  {!survey.viewed ? (
                    <Button
                      variant="basic-primary"
                      size="medium"
                      onClick={handleMarkAsViewed}
                    >
                      Mark as Reviewed
                    </Button>
                  ) : (
                    <Button
                      variant="outline-primary"
                      size="medium"
                      onClick={handleMarkAsUnviewed}
                      disabled={survey.viewedBy?.uuid !== user?.uuid}
                    >
                      Mark as Not Reviewed
                    </Button>
                  )}
                </>
              )}
            </Authorizor>
          </div>
        </div>
      )}
    </BaseLayout>
  );
}
